/**
 * Exams & Marks (Phase 3 / Module 4) validation contracts.
 *
 * Mirrors the schema in `packages/db/src/schema/exams.ts`. Used by both the
 * API route handlers (input validation) and downstream type sharing.
 *
 * Marks are stored as INTEGERS in the database (see `exam_marks.marks_obtained`).
 * The schemas here therefore use `z.number().int()` — we do not accept
 * fractional marks. Half/quarter-mark grading schemes can be supported later
 * by storing in tenths-of-a-mark; for now the convention is whole numbers.
 */
import { z } from 'zod';
import { dateStringSchema, idSchema, paginationSchema } from './common.js';

export const examCycleTypeSchema = z.enum([
  'monthly',
  'half_yearly',
  'annual',
  'other',
]);

export const examCycleStatusSchema = z.enum([
  'draft',
  'active',
  'marks_locked',
  'archived',
]);

const academicYearSchema = z
  .string()
  .trim()
  .min(4, 'Academic year is required')
  .max(20, 'Academic year is too long');

const cycleNameSchema = z.string().trim().min(2).max(160);
const cycleTermSchema = z.string().trim().min(1).max(40);
const cycleDescriptionSchema = z.string().trim().min(1).max(2000);
const subjectNameSchema = z.string().trim().min(2).max(160);
const remarksSchema = z.string().trim().min(1).max(1000);
const noteSchema = z.string().trim().min(1).max(1000);

const dateOrderRefinement = (data: {
  startDate?: string | undefined;
  endDate?: string | undefined;
}): boolean =>
  !data.startDate || !data.endDate || data.startDate <= data.endDate;

// ----- Cycles --------------------------------------------------------------

export const examCycleCreateSchema = z
  .object({
    academicYear: academicYearSchema,
    term: cycleTermSchema.optional(),
    type: examCycleTypeSchema,
    name: cycleNameSchema,
    description: cycleDescriptionSchema.optional(),
    startDate: dateStringSchema,
    endDate: dateStringSchema,
  })
  .strict()
  .refine(dateOrderRefinement, {
    message: 'startDate cannot be after endDate',
    path: ['startDate'],
  });

export const examCycleUpdateSchema = z
  .object({
    academicYear: academicYearSchema.optional(),
    term: cycleTermSchema.nullable().optional(),
    type: examCycleTypeSchema.optional(),
    name: cycleNameSchema.optional(),
    description: cycleDescriptionSchema.nullable().optional(),
    startDate: dateStringSchema.optional(),
    endDate: dateStringSchema.optional(),
  })
  .strict()
  .refine(dateOrderRefinement, {
    message: 'startDate cannot be after endDate',
    path: ['startDate'],
  });

/**
 * Query contract for `GET /api/exams/cycles`.
 *
 * `status` and `type` are DELIBERATELY ABSENT from this schema. They were
 * once single-value Zod enums here, but the list endpoint now accepts
 * comma-separated multi-select values (`?status=active,marks_locked`,
 * `?type=monthly,annual`) — a comma value would fail a single-enum parse
 * with a 400. Following the claims / probation-queue precedent, the route
 * reads those two params straight off `url.searchParams` and validates them
 * with `parseEnumListFilter` (which drops unknown tokens safely), so this
 * strict schema must NOT see them (`paginationSchema` is `.strict()`, so an
 * unknown key here would 400). Only the params that remain single-valued
 * live in this schema.
 *
 * The `term`, `startFrom`/`startTo` (date range), `q` (name search), and
 * `sort` params are likewise parsed off `url.searchParams` in the route
 * (via `parseDateOnly` / `parseSort` / plain trim) rather than modelled
 * here, to keep this schema focused on the pagination + single-value facets
 * and to reuse the shared list-filter helpers for the rest.
 */
export const examCycleListQuerySchema = paginationSchema.extend({
  academicYear: academicYearSchema.optional(),
});

// ----- Subjects ------------------------------------------------------------

const subjectMaxMarksSchema = z.number().int().min(1).max(1000);
const subjectPassMarksSchema = z.number().int().min(0).max(1000);
const subjectOrderIndexSchema = z.number().int().min(0).max(10_000);

export const examSubjectCreateSchema = z
  .object({
    subjectName: subjectNameSchema,
    courseId: idSchema.nullable().optional(),
    maxMarks: subjectMaxMarksSchema.default(100),
    passMarks: subjectPassMarksSchema.default(40),
    orderIndex: subjectOrderIndexSchema.default(0),
  })
  .strict()
  .refine((data) => data.passMarks <= data.maxMarks, {
    message: 'passMarks cannot exceed maxMarks',
    path: ['passMarks'],
  });

export const examSubjectUpdateSchema = z
  .object({
    subjectName: subjectNameSchema.optional(),
    courseId: idSchema.nullable().optional(),
    maxMarks: subjectMaxMarksSchema.optional(),
    passMarks: subjectPassMarksSchema.optional(),
    orderIndex: subjectOrderIndexSchema.optional(),
  })
  .strict()
  .refine(
    (data) =>
      data.passMarks === undefined ||
      data.maxMarks === undefined ||
      data.passMarks <= data.maxMarks,
    {
      message: 'passMarks cannot exceed maxMarks',
      path: ['passMarks'],
    },
  );

// ----- Marks ---------------------------------------------------------------

const marksObtainedSchema = z.number().int().min(0).max(10_000);

export const markEntrySchema = z
  .object({
    studentId: idSchema,
    marksObtained: marksObtainedSchema.nullable().optional(),
    isAbsent: z.boolean().default(false),
    remarks: remarksSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (data) =>
      data.isAbsent ||
      data.marksObtained !== undefined ||
      // Allow `null` as an explicit "clear" signal; client may send null when
      // un-recording a previously entered mark.
      true,
    { message: 'marksObtained is required when isAbsent is false' },
  );

export const markUploadSchema = z
  .object({
    entries: z.array(markEntrySchema).min(1).max(200),
  })
  .strict();

export const markUpdateSchema = z
  .object({
    marksObtained: marksObtainedSchema.nullable().optional(),
    isAbsent: z.boolean().optional(),
    remarks: remarksSchema.nullable().optional(),
  })
  .strict();

// ----- Visibility ----------------------------------------------------------

export const examCycleVisibilitySchema = z
  .object({
    isVisible: z.boolean(),
    note: noteSchema.optional(),
    /**
     * Set to `true` to override the completeness check when publishing
     * (isVisible: true) a cycle where some students don't yet have
     * marks for every subject. Per docs/newreq.md Module 4, the
     * default behaviour is to refuse the publish with a 422 and
     * surface the gap so the staff can decide.
     */
    confirmIncompletePublish: z.boolean().optional(),
  })
  .strict();

export const examStudentVisibilitySchema = z
  .object({
    isVisible: z.boolean(),
    note: noteSchema.optional(),
  })
  .strict();

// ----- Response shapes -----------------------------------------------------

const isoTimestampSchema = z.string().min(1);

export const examCycleResponseSchema = z.object({
  id: z.string(),
  academicYear: z.string(),
  term: z.string().nullable(),
  type: examCycleTypeSchema,
  name: z.string(),
  description: z.string().nullable(),
  startDate: z.string(),
  endDate: z.string(),
  status: examCycleStatusSchema,
  createdBy: z.string(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
});

export const examSubjectResponseSchema = z.object({
  id: z.string(),
  cycleId: z.string(),
  courseId: z.string().nullable(),
  subjectName: z.string(),
  maxMarks: z.number().int(),
  passMarks: z.number().int(),
  orderIndex: z.number().int(),
  createdAt: isoTimestampSchema,
});

export const examMarkResponseSchema = z.object({
  id: z.string(),
  cycleId: z.string(),
  subjectId: z.string(),
  studentId: z.string(),
  marksObtained: z.number().int().nullable(),
  isAbsent: z.boolean(),
  remarks: z.string().nullable(),
  recordedBy: z.string().nullable(),
  recordedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export const examCycleListItemSchema = examCycleResponseSchema.extend({
  subjectCount: z.number().int().min(0),
  marksRecordedCount: z.number().int().min(0),
});

export const examCycleListResponseSchema = z.object({
  items: z.array(examCycleListItemSchema),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().min(0),
});

export const examCycleDetailResponseSchema = examCycleResponseSchema.extend({
  subjects: z.array(
    examSubjectResponseSchema.extend({
      marksRecordedCount: z.number().int().min(0),
    }),
  ),
  marksRecordedCount: z.number().int().min(0),
  visibility: z.object({
    cycleDefault: z.boolean(),
    cycleOverrideExists: z.boolean(),
    studentOverridesCount: z.number().int().min(0),
  }),
});

const marksGridRowSchema = z.object({
  studentId: z.string(),
  studentName: z.string(),
  admissionNumber: z.string(),
  batchId: z.string().nullable(),
  batchName: z.string().nullable(),
  mark: z
    .object({
      id: z.string(),
      marksObtained: z.number().int().nullable(),
      isAbsent: z.boolean(),
      remarks: z.string().nullable(),
      recordedBy: z.string().nullable(),
      recordedAt: z.string().nullable(),
      updatedBy: z.string().nullable(),
      updatedAt: z.string().nullable(),
    })
    .nullable(),
});

export const marksGridResponseSchema = z.object({
  cycle: examCycleResponseSchema,
  subject: examSubjectResponseSchema,
  rows: z.array(marksGridRowSchema),
});

export const markUploadResponseSchema = z.object({
  recorded: z.number().int().min(0),
  skipped: z.array(
    z.object({
      studentId: z.string(),
      reason: z.enum([
        'not_enrolled',
        'invalid_marks',
        'invalid_range',
        'unknown_student',
      ]),
    }),
  ),
});

export const cycleResultsResponseSchema = z.object({
  cycle: examCycleResponseSchema,
  perSubject: z.array(
    z.object({
      subjectId: z.string(),
      subjectName: z.string(),
      maxMarks: z.number().int(),
      passMarks: z.number().int(),
      recordedCount: z.number().int().min(0),
      absentCount: z.number().int().min(0),
      passedCount: z.number().int().min(0),
      failedCount: z.number().int().min(0),
      averageMarks: z.number().nullable(),
      highestMarks: z.number().int().nullable(),
      lowestMarks: z.number().int().nullable(),
    }),
  ),
  perStudent: z.array(
    z.object({
      studentId: z.string(),
      studentName: z.string(),
      admissionNumber: z.string(),
      totalMarksObtained: z.number().int(),
      totalMaxMarks: z.number().int(),
      subjectsAttempted: z.number().int().min(0),
      subjectsPassed: z.number().int().min(0),
      averagePercent: z.number().nullable(),
    }),
  ),
});

const studentMarkItemSchema = z.object({
  cycleId: z.string(),
  cycleName: z.string(),
  academicYear: z.string(),
  type: examCycleTypeSchema,
  subjectId: z.string(),
  subjectName: z.string(),
  maxMarks: z.number().int(),
  passMarks: z.number().int(),
  marksObtained: z.number().int().nullable(),
  isAbsent: z.boolean(),
  remarks: z.string().nullable(),
  recordedAt: z.string().nullable(),
});

export const selfExamMarksResponseSchema = z.object({
  items: z.array(studentMarkItemSchema),
});

// ----- Type exports --------------------------------------------------------

export type ExamCycleCreateInput = z.infer<typeof examCycleCreateSchema>;
export type ExamCycleUpdateInput = z.infer<typeof examCycleUpdateSchema>;
export type ExamCycleListQuery = z.infer<typeof examCycleListQuerySchema>;
export type ExamSubjectCreateInput = z.infer<typeof examSubjectCreateSchema>;
export type ExamSubjectUpdateInput = z.infer<typeof examSubjectUpdateSchema>;
export type MarkEntryInput = z.infer<typeof markEntrySchema>;
export type MarkUploadInput = z.infer<typeof markUploadSchema>;
export type MarkUpdateInput = z.infer<typeof markUpdateSchema>;
export type ExamCycleVisibilityInput = z.infer<typeof examCycleVisibilitySchema>;
export type ExamStudentVisibilityInput = z.infer<
  typeof examStudentVisibilitySchema
>;
export type ExamCycleResponse = z.infer<typeof examCycleResponseSchema>;
export type ExamSubjectResponse = z.infer<typeof examSubjectResponseSchema>;
export type ExamMarkResponse = z.infer<typeof examMarkResponseSchema>;
export type ExamCycleListResponse = z.infer<typeof examCycleListResponseSchema>;
export type ExamCycleDetailResponse = z.infer<
  typeof examCycleDetailResponseSchema
>;
export type MarksGridResponse = z.infer<typeof marksGridResponseSchema>;
export type MarkUploadResponse = z.infer<typeof markUploadResponseSchema>;
export type CycleResultsResponse = z.infer<typeof cycleResultsResponseSchema>;
export type SelfExamMarksResponse = z.infer<typeof selfExamMarksResponseSchema>;
